/* ============================================================================
   render.ts — utilities for rendering an ASCII grid (string[][]).
   Two modes:
     - renderAsciiToString: simple `\n`-joined string for a <pre>.
     - renderAsciiToCanvas: 2D canvas drawing (more controllable, GPU-accelerated
       via composite, allows per-cell color/transform).

   For MVP we use the first one (Option A in brief) — <pre> with CSS transforms
   modulated by hand tracking is plenty performant for grids up to ~200×120.
   ============================================================================ */

import type { CanvasParams } from '../canvasStore';

/** Joins grid rows with `\n`. Cells are single chars. */
export function renderAsciiToString(grid: string[][]): string {
  let out = '';
  for (let r = 0; r < grid.length; r++) {
    if (r > 0) out += '\n';
    out += grid[r]!.join('');
  }
  return out;
}

export interface CanvasRenderTransform {
  offsetX?: number;
  offsetY?: number;
  scale?: number;
  hueShift?: number; // pas utilisé en grayscale, reserved
}

/**
 * Dessine la grille sur un canvas 2D. Reuse le canvas (set width = ...).
 * Performance : font set once, fillText par cell.
 */
export function renderAsciiToCanvas(
  grid: string[][],
  params: Pick<CanvasParams, 'fontSize' | 'letterSpacing' | 'lineHeight'>,
  canvas: HTMLCanvasElement,
  fg: string,
  bg: string,
  transform?: CanvasRenderTransform,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const fs = params.fontSize;
  const cw = fs * 0.6 + params.letterSpacing; // approx mono char width
  const ch = fs * params.lineHeight;
  const cols = grid[0]?.length ?? 0;
  const rows = grid.length;
  // Sizing
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const W = Math.ceil(cols * cw);
  const H = Math.ceil(rows * ch);
  if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  if (transform) {
    const { offsetX = 0, offsetY = 0, scale = 1 } = transform;
    ctx.translate(W / 2 + offsetX, H / 2 + offsetY);
    ctx.scale(scale, scale);
    ctx.translate(-W / 2, -H / 2);
  }

  ctx.fillStyle = fg;
  ctx.font = `${fs}px ui-monospace, "JetBrains Mono", Menlo, monospace`;
  ctx.textBaseline = 'top';

  for (let r = 0; r < rows; r++) {
    const row = grid[r]!;
    const y = r * ch;
    for (let c = 0; c < cols; c++) {
      const g = row[c];
      if (!g || g === ' ') continue;
      ctx.fillText(g, c * cw, y);
    }
  }
}
